import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const iconOptions = [
    { value: 'UsersIcon', label: 'Users' },
    { value: 'BuildingOfficeIcon', label: 'Building Office' },
    { value: 'CalendarIcon', label: 'Calendar' },
    { value: 'CurrencyRupeeIcon', label: 'Currency Rupee' },
    { value: 'ChartBarIcon', label: 'Chart Bar' },
    { value: 'TrophyIcon', label: 'Trophy' },
    { value: 'GlobeAltIcon', label: 'Globe' },
    { value: 'BanknotesIcon', label: 'Banknotes' }
];

const colorOptions = [
    { value: 'text-primary-600', label: 'Primary Blue' },
    { value: 'text-green-600', label: 'Green' },
    { value: 'text-blue-600', label: 'Blue' },
    { value: 'text-purple-600', label: 'Purple' },
    { value: 'text-orange-600', label: 'Orange' },
    { value: 'text-red-600', label: 'Red' }
];

const StatModal = ({ stat, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        icon_name: 'UsersIcon',
        value: 0,
        suffix: '+',
        label: '',
        color_class: 'text-primary-600',
        display_order: 0,
        is_active: true
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (stat) {
            setFormData({
                icon_name: stat.icon_name || 'UsersIcon',
                value: stat.value || 0,
                suffix: stat.suffix || '+',
                label: stat.label || '',
                color_class: stat.color_class || 'text-primary-600',
                display_order: stat.display_order || 0,
                is_active: stat.is_active !== undefined ? stat.is_active : true
            });
        }
    }, [stat]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (name === 'value' || name === 'display_order' ? parseInt(value) || 0 : value)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (stat) {
                await axios.put(`/api/admin/whychoose/stats/${stat.id}`, formData);
                toast.success('Stat updated successfully');
            } else {
                await axios.post('/api/admin/whychoose/stats', formData);
                toast.success('Stat created successfully');
            }
            onSave();
        } catch (error) {
            console.error('Error saving stat:', error);
            toast.error('Failed to save stat');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-100">
                    <h2 className="text-xl font-bold text-slate-800">
                        {stat ? 'Edit Stat' : 'Add New Stat'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <XMarkIcon className="h-5 w-5 text-slate-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                    {/* Icon Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Icon
                        </label>
                        <select
                            name="icon_name"
                            value={formData.icon_name}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                        >
                            {iconOptions.map(icon => (
                                <option key={icon.value} value={icon.value}>
                                    {icon.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Value */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Value *
                        </label>
                        <input
                            type="number"
                            name="value"
                            value={formData.value}
                            onChange={handleChange}
                            required
                            min="0"
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                            placeholder="e.g., 50000"
                        />
                    </div>

                    {/* Suffix */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Suffix
                        </label>
                        <input
                            type="text"
                            name="suffix"
                            value={formData.suffix}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                            placeholder="e.g., + or Cr+"
                        />
                    </div>

                    {/* Label */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Label *
                        </label>
                        <input
                            type="text"
                            name="label"
                            value={formData.label}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                            placeholder="e.g., Happy Customers"
                        />
                    </div>

                    {/* Color Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Color Theme
                        </label>
                        <select
                            name="color_class"
                            value={formData.color_class}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                        >
                            {colorOptions.map(color => (
                                <option key={color.value} value={color.value}>
                                    {color.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Display Order */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Display Order
                        </label>
                        <input
                            type="number"
                            name="display_order"
                            value={formData.display_order}
                            onChange={handleChange}
                            min="0"
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all"
                        />
                    </div>

                    {/* Active Toggle */}
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            name="is_active"
                            checked={formData.is_active}
                            onChange={handleChange}
                            className="w-5 h-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                        />
                        <label className="text-sm font-medium text-slate-700">
                            Active (visible on website)
                        </label>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 px-6 py-3 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
                        >
                            {loading ? 'Saving...' : (stat ? 'Update' : 'Create')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default StatModal;
